import { apiMain } from 'services/apiService';
import { MenuItem, MenuItemExtras } from 'types/menuTypes';
import { logger } from 'utils/logger';

export interface Form {
  id: string;
  form_name: string;
  has_dashboard: boolean;
  description: string | null;
}

export interface FormGroup {
  id: string;
  name: string;
  slug: string;
  forms: Form[];
}

export interface FormResponse<T = Form> {
  form: T | null;
  isLoading: boolean;
  error: string | null;
}

type FormExtras = {
  form_name?: string;
  form_group_name?: string;
  form_group_id?: string;
} & MenuItemExtras;

const getFormNameFromMenu = (
  menu: MenuItem,
  componentType: 'FORM_DASHBOARD' | 'FORM'
): string | null => {
  if (menu.feature?.dynamic_component === componentType) {
    const extras = menu.feature.extras as FormExtras;
    return extras?.form_name || extras?.form_group_name || null;
  }
  return null;
};

export const getFormIdByTitle = async (
  activeThirdLevelMenu: MenuItem[],
  activeDocumentId: string
): Promise<FormResponse<Form>> => {
  try {
    const currentMenu = activeThirdLevelMenu.find(
      menu => menu.feature?.documentId === activeDocumentId
    );
    if (!currentMenu) {
      return {
        form: null,
        isLoading: false,
        error: 'Menu item not found',
      };
    }

    const formName = getFormNameFromMenu(currentMenu, 'FORM_DASHBOARD') || currentMenu.label;

    if (!formName) {
      return {
        form: null,
        isLoading: false,
        error: 'Form name not found in menu configuration',
      };
    }

    const response = await apiMain.get<FormGroup[]>(`/forms/v1/groups/all`);

    // Flatten all forms from all groups
    const allForms = response.data.flatMap(group => group.forms);

    const matchingForm = allForms.find(
      form => form.form_name.toLowerCase() === formName.toLowerCase()
    );

    if (!matchingForm) {
      return {
        form: null,
        isLoading: false,
        error: `Formulário "${formName}" não encontrado.`,
      };
    }

    return {
      form: matchingForm,
      isLoading: false,
      error: null,
    };
  } catch (error) {
    logger.error('Error fetching form:', error);
    return {
      form: null,
      isLoading: false,
      error: 'Error fetching form information',
    };
  }
};

export const getFormIdByFormType = async (
  activeThirdLevelMenu: MenuItem[],
  activeDocumentId: string
): Promise<FormResponse<Form[]>> => {
  try {
    const currentMenu = activeThirdLevelMenu.find(
      menu => menu.feature?.documentId === activeDocumentId
    );
    if (!currentMenu) {
      return {
        form: null,
        isLoading: false,
        error: 'Menu item not found',
      };
    }

    const formName = getFormNameFromMenu(currentMenu, 'FORM') || currentMenu.label;

    if (!formName) {
      return {
        form: null,
        isLoading: false,
        error: 'Form name not found in menu configuration',
      };
    }

    const response = await apiMain.get<FormGroup[]>(`/forms/v1/groups/all`);

    // Find the group that matches the form name
    const matchingGroup = response.data.find(
      group => group.name.toLowerCase() === formName.toLowerCase()
    );

    if (!matchingGroup) {
      return {
        form: null,
        isLoading: false,
        error: `Grupo de formulários "${formName}" não encontrado.`,
      };
    }

    return {
      form: matchingGroup.forms,
      isLoading: false,
      error: null,
    };
  } catch (error) {
    logger.error('Error fetching form:', error);
    return {
      form: null,
      isLoading: false,
      error: 'Error fetching form information',
    };
  }
};

export const getFormGroupIdByName = async (groupName: string): Promise<string | null> => {
  try {
    const response = await apiMain.get<FormGroup[]>(`/forms/v1/groups/all`);
    const group = response.data.find(g => g.name.toLowerCase() === groupName.toLowerCase());
    return group ? group.id : null;
  } catch (error) {
    logger.error('Error fetching form group:', error);
    return null;
  }
};

/**
 * Find menu item by form group ID
 */
export const findMenuByFormGroupId = (menu: MenuItem[], formGroupId: string): MenuItem | null => {
  logger.debug('[FormMapper] Finding menu by form group ID:', { menu, formGroupId });
  const searchInMenu = (items: MenuItem[]): MenuItem | null => {
    for (const item of items) {
      // Check if this menu item has the form group ID in its extras
      if (item.feature?.extras && 'form_group_id' in item.feature.extras) {
        const extras = item.feature.extras as FormExtras;
        if (extras.form_group_id === formGroupId) {
          return item;
        }
      }

      // Search in submenus
      if (item.subMenu?.length) {
        const found = searchInMenu(item.subMenu);
        if (found) return found;
      }
    }
    return null;
  };

  return searchInMenu(menu);
};
